import React, { useEffect, useMemo, useState } from 'react';
import * as Recharts from 'recharts';
import { isEmpty } from 'lodash';

import ChartCard from '../../ChartComponents/ChartCard';
import ChartTooltip from '../../ChartComponents/ChartTooltip';
import Info from '../../../../components/Info';
import { compareAgeBuckets, getAgeBucketColor } from '../constants';
import {
  axisTick,
  chartMargin,
  formatAxisTime,
  tickEvery,
} from '../chartShared';
import style from '../style.module.scss';

const AgeTimelineChart = ({ data }) => {
  const ageTimeline = data?.ageTimeline || {};
  const ageBuckets = useMemo(
    () => Object.keys(ageTimeline).sort(compareAgeBuckets),
    [ageTimeline],
  );
  const [chartLines, setChartLines] = useState({});

  useEffect(() => {
    setChartLines(ageBuckets.reduce((acc, bucket) => {
      acc[bucket] = true;
      return acc;
    }, {}));
  }, [ageBuckets]);

  // Merge by timestamp — age buckets omit zeros, so index zip would misalign series.
  const chartData = useMemo(() => {
    if (isEmpty(ageBuckets)) {
      return [];
    }

    const byTime = new Map();
    ageBuckets.forEach((bucket) => {
      (ageTimeline[bucket] || []).forEach(({ x, y }) => {
        if (!x) return;
        const point = byTime.get(x) || { time: x };
        point[bucket] = y || 0;
        byTime.set(x, point);
      });
    });

    if (!byTime.size) {
      return [];
    }

    return [...byTime.values()]
      .sort((a, b) => String(a.time).localeCompare(String(b.time)))
      .map((point) => {
        const row = { ...point };
        ageBuckets.forEach((bucket) => {
          if (row[bucket] == null) row[bucket] = 0;
        });
        return row;
      });
  }, [ageTimeline, ageBuckets]);

  const handleLegendClick = (series) => {
    setChartLines({ ...chartLines, [series]: !chartLines[series] });
  };

  if (isEmpty(chartData)) {
    return null;
  }

  return (
    <ChartCard
      title={(
        <Info
          label="Age"
          content="Face counts by age group (Rekognition AgeRange midpoint) per scan session"
          className="ml-2"
        />
      )}
    >
      <div className={style.chartFix}>
        <Recharts.ResponsiveContainer width="100%" height={400}>
          <Recharts.LineChart data={chartData} margin={chartMargin} isAnimationActive={false}>
            <Recharts.CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" vertical={false} />
            <Recharts.XAxis
              dataKey="time"
              type="category"
              tickLine
              axisLine={{ stroke: '#B7B8BA' }}
              interval={tickEvery(chartData.length)}
              tick={axisTick}
              tickFormatter={formatAxisTime}
            />
            <Recharts.YAxis
              tickLine
              axisLine={{ stroke: '#B7B8BA' }}
              width={52}
              tick={axisTick}
              label={{
                value: 'Face count',
                angle: -90,
                position: 'insideLeft',
                style: { fill: '#4E5155', fontSize: 12, textAnchor: 'middle' },
              }}
            />
            <Recharts.Tooltip
              content={<ChartTooltip />}
              labelFormatter={(label) => `Scan ${label}`}
            />
            <Recharts.Legend
              verticalAlign="top"
              align="right"
              wrapperStyle={{ fontSize: '14px', color: '#4E5155' }}
              iconType="circle"
              iconSize="11"
              onClick={({ dataKey }) => handleLegendClick(dataKey)}
            />
            {ageBuckets.map((bucket, index) => (
              <Recharts.Line
                key={bucket}
                type="monotone"
                dataKey={bucket}
                name={bucket}
                stroke={getAgeBucketColor(bucket, index)}
                fill={getAgeBucketColor(bucket, index)}
                dot={false}
                strokeWidth={2}
                hide={!chartLines[bucket]}
              />
            ))}
          </Recharts.LineChart>
        </Recharts.ResponsiveContainer>
      </div>
    </ChartCard>
  );
};

export default AgeTimelineChart;
